import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useCreatePtoRequest } from '@/features/pto/hooks';
import {
  BEREAVEMENT_RELATIONSHIPS,
  countWeekdays,
  type BereavementRelationship,
} from '@/features/pto/format';
import type { PtoLeaveType } from '@/types/database';

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

interface RequestPtoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Days still available this year — shown as a hint / soft warning for PTO. */
  remainingDays?: number;
}

/**
 * "Request time off" pop-up. Leave type is PTO (counts against the annual
 * balance) or Bereavement (does not). Bereavement requires the relationship
 * of the deceased to the employee. Day count is pre-filled from weekdays in
 * the range and stays editable.
 */
export function RequestPtoDialog({
  open,
  onOpenChange,
  remainingDays,
}: RequestPtoDialogProps) {
  const [leaveType, setLeaveType] = useState<PtoLeaveType>('pto');
  const [relationship, setRelationship] = useState<BereavementRelationship | ''>(
    '',
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [days, setDays] = useState('');
  const [daysTouched, setDaysTouched] = useState(false);
  const [reason, setReason] = useState('');

  const create = useCreatePtoRequest();
  const isBereavement = leaveType === 'bereavement';

  // Re-seed the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const today = todayKey();
    setLeaveType('pto');
    setRelationship('');
    setStartDate(today);
    setEndDate(today);
    setDays('1');
    setDaysTouched(false);
    setReason('');
  }, [open]);

  // Auto-fill the day count from the weekday span until the user edits it by
  // hand (then we respect their number).
  useEffect(() => {
    if (daysTouched) return;
    const weekdays = countWeekdays(startDate, endDate);
    if (weekdays > 0) setDays(String(weekdays));
  }, [startDate, endDate, daysTouched]);

  const numericDays = Number(days);
  const overBalance =
    !isBereavement &&
    remainingDays != null &&
    Number.isFinite(numericDays) &&
    numericDays > remainingDays;

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      toast.error('Pick a start and end date.');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error('The end date must be on or after the start date.');
      return;
    }
    if (!Number.isFinite(numericDays) || numericDays <= 0) {
      toast.error(
        isBereavement
          ? 'Enter how many days of bereavement leave this covers.'
          : 'Enter how many PTO days this request uses.',
      );
      return;
    }
    if (isBereavement && !relationship) {
      toast.error('Select the relationship to you.');
      return;
    }
    try {
      await create.mutateAsync({
        start_date: startDate,
        end_date: endDate,
        days: numericDays,
        reason: reason.trim() || null,
        leave_type: leaveType,
        bereavement_relationship: isBereavement ? relationship : null,
      });
      toast.success(
        isBereavement
          ? 'Bereavement leave submitted for approval.'
          : 'PTO request submitted for approval.',
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not submit your request.',
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Request time off</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="pto-leave-type">Leave type</Label>
            <Select
              value={leaveType}
              onValueChange={(v) => {
                const next = v as PtoLeaveType;
                setLeaveType(next);
                if (next !== 'bereavement') setRelationship('');
              }}
            >
              <SelectTrigger id="pto-leave-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pto">PTO</SelectItem>
                <SelectItem value="bereavement">Bereavement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isBereavement && (
            <div className="grid gap-1.5">
              <Label htmlFor="pto-relationship">Relationship to you</Label>
              <Select
                value={relationship || undefined}
                onValueChange={(v) =>
                  setRelationship(v as BereavementRelationship)
                }
              >
                <SelectTrigger id="pto-relationship">
                  <SelectValue placeholder="Select relationship…" />
                </SelectTrigger>
                <SelectContent>
                  {BEREAVEMENT_RELATIONSHIPS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="pto-start">Start date</Label>
              <Input
                id="pto-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pto-end">End date</Label>
              <Input
                id="pto-end"
                type="date"
                min={startDate || undefined}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="pto-days">
              {isBereavement ? 'Days off' : 'PTO days used'}
            </Label>
            <Input
              id="pto-days"
              type="number"
              min="0.5"
              step="0.5"
              value={days}
              onChange={(e) => {
                setDaysTouched(true);
                setDays(e.target.value);
              }}
              className="sm:max-w-[8rem]"
            />
            {overBalance && (
              <p className="text-xs text-fresh-salmon-dark">
                Heads up: this is more than your remaining balance of{' '}
                {remainingDays} day{remainingDays === 1 ? '' : 's'}. You can
                still submit — your approver will decide.
              </p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="pto-reason">
              {isBereavement ? 'Notes (optional)' : 'Reason (optional)'}
            </Label>
            <Textarea
              id="pto-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                isBereavement
                  ? 'Any additional context for your manager…'
                  : 'e.g. Family vacation'
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
